import { useEffect, useMemo, useState } from 'react';

import { formatTime } from '@apps/web/lib/date_utils';
import {
    type ContactAttempt,
    type ContactAttemptOutreachStatus,
} from '@libraries/typescript/api_client';

const NON_TERMINAL_CONTACT_ATTEMPT_STATUSES =
    new Set<ContactAttemptOutreachStatus>([
        'queued',
        'initiated',
        'in_progress',
        'reviewing_call',
    ]);

const DELAY_AFTER_SHIFT_END_MINUTES = 10;

const formatCountdownLabel = (seconds: number): string => {
    const safeSeconds = Math.max(0, seconds);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    if (minutes === 0) {
        return `${remainingSeconds}s`;
    }
    return `${minutes}m:${remainingSeconds.toString().padStart(2, '0')}s`;
};

export type OutreachStep = {
    readonly step_type: ContactAttempt['delivery_mode'];
    readonly delay_in_minutes: number;
    readonly message_template?: string | null;
    readonly prompt_alteration?: string | null;
};

type NextAttemptCountdownProps = {
    readonly attempt: ContactAttempt;
    readonly outreachSteps: ReadonlyArray<OutreachStep>;
    readonly pausedTimeSeconds?: number;
    readonly isPaused?: boolean;
    readonly currentShiftEndTime?: string | null;
    readonly skipBusyCaregivers?: boolean;
    readonly timezone?: string;
};

export function NextAttemptCountdown({
    attempt,
    outreachSteps,
    pausedTimeSeconds = 0,
    isPaused = false,
    currentShiftEndTime,
    skipBusyCaregivers = false,
    timezone = 'UTC',
}: NextAttemptCountdownProps) {
    const [countdownSeconds, setCountdownSeconds] = useState<number | null>(
        null,
    );
    const hasDefinitiveShiftResponse =
        attempt.shift_responses &&
        Object.values(attempt.shift_responses).some(
            (r) => r !== 'pending' && r !== 'no_response' && r !== 'unknown',
        );

    const shouldHideCountdown = hasDefinitiveShiftResponse;

    const nextAttemptTime = useMemo(() => {
        if (shouldHideCountdown) {
            return null;
        }

        // Don't show countdown if attempt is still in progress
        if (
            NON_TERMINAL_CONTACT_ATTEMPT_STATUSES.has(attempt.outreach_status)
        ) {
            return null;
        }

        if (!outreachSteps || outreachSteps.length === 0) {
            return null;
        }

        const currentRound =
            typeof attempt.round === 'number' ? attempt.round : -1;

        if (currentRound === -1) {
            return null;
        }

        const nextStep = outreachSteps[currentRound + 1];
        if (!nextStep) {
            return null;
        }

        const waitMinutes = nextStep.delay_in_minutes;
        if (waitMinutes <= 0) {
            return null;
        }

        const attemptCreatedAt = new Date(attempt.created_at);
        if (Number.isNaN(attemptCreatedAt.getTime())) {
            return null;
        }

        const target = new Date(attemptCreatedAt);
        target.setMinutes(target.getMinutes() + waitMinutes);
        // Add paused time to the target
        target.setSeconds(target.getSeconds() + pausedTimeSeconds);

        // If skip_busy_caregivers is enabled and caregiver is on shift,
        // the next voice attempt will be delayed until after the shift ends
        if (
            skipBusyCaregivers &&
            currentShiftEndTime &&
            nextStep.step_type === 'voice'
        ) {
            const shiftEndDate = new Date(currentShiftEndTime);
            if (!Number.isNaN(shiftEndDate.getTime())) {
                const shiftBasedTarget = new Date(shiftEndDate);
                shiftBasedTarget.setMinutes(
                    shiftBasedTarget.getMinutes() +
                        DELAY_AFTER_SHIFT_END_MINUTES,
                );
                // Use the later of the two targets
                if (shiftBasedTarget.getTime() > target.getTime()) {
                    return {
                        target: shiftBasedTarget,
                        deliveryMode: nextStep.step_type,
                        isDeferred: true,
                    };
                }
            }
        }

        return {
            target,
            deliveryMode: nextStep.step_type,
            isDeferred: false,
        };
    }, [
        attempt,
        outreachSteps,
        pausedTimeSeconds,
        skipBusyCaregivers,
        currentShiftEndTime,
        shouldHideCountdown,
    ]);

    useEffect(() => {
        if (!nextAttemptTime) {
            setCountdownSeconds(null);
            return;
        }

        const updateCountdown = () => {
            const now = new Date();
            const diff = nextAttemptTime.target.getTime() - now.getTime();

            if (diff <= 0) {
                setCountdownSeconds((previous) =>
                    previous === 0 ? previous : 0,
                );
                return true;
            }

            setCountdownSeconds(Math.ceil(diff / 1000));
            return false;
        };

        const shouldStopImmediately = updateCountdown();
        if (shouldStopImmediately) {
            return;
        }

        const interval = setInterval(() => {
            const shouldStop = updateCountdown();
            if (shouldStop) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [nextAttemptTime]);

    if (shouldHideCountdown) {
        return null;
    }

    if (!nextAttemptTime || countdownSeconds === null) {
        return <span className="text-muted-foreground text-xs">-</span>;
    }

    if (isPaused) {
        return (
            <span className="text-muted-foreground text-xs italic">
                Outreach Paused
            </span>
        );
    }

    if (nextAttemptTime.isDeferred) {
        return (
            <span className="text-muted-foreground text-xs italic">
                Deferred until{' '}
                {formatTime(nextAttemptTime.target.toISOString(), timezone)}
            </span>
        );
    }

    return (
        <div className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
            <span className={countdownSeconds > 0 ? 'text-blue-600' : ''}>
                {formatCountdownLabel(countdownSeconds)}
            </span>
        </div>
    );
}
